import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-bootstrap page.
 *
 * Purpose: hand the admin role to a real account when the seeded
 * admin@brickandcapital.com user is locked behind the mandatory-admin-2FA
 * gate (or otherwise unreachable). Gated by the ADMIN_BOOTSTRAP_SECRET
 * env var on the backend.
 *
 * Visit: /admin-bootstrap?secret=YOUR_RENDER_ENV_SECRET
 *
 * Workflow once the new admin is granted:
 *   1. Stay signed in.
 *   2. Settings → Security → enable Two-Factor Authentication, scan QR,
 *      save backup codes.
 *   3. Sign out and back in to confirm the 2FA challenge fires cleanly.
 *   4. Remove ADMIN_BOOTSTRAP_SECRET on Render.
 */
const AdminBootstrapPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const secret = params.get('secret') || '';
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Prefill with the logged-in user's email — the common case is "promote
  // me, the founder, sitting in front of the screen".
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const canSubmit = !!secret && !!email && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/grant-admin`,
        { email: email.trim().toLowerCase() },
        { headers: { 'X-Admin-Bootstrap-Secret': secret } },
      );
      setResult(data);
      try { await refreshUser?.(); } catch (_) {}
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Could not grant admin.';
      if (status === 404 && /not found/i.test(String(detail))) {
        setError('Admin bootstrap is disabled. Set ADMIN_BOOTSTRAP_SECRET on Render and redeploy.');
      } else if (status === 403) {
        setError('Wrong secret. Check the value in your URL matches ADMIN_BOOTSTRAP_SECRET on Render.');
      } else {
        setError(String(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-[#1F3A68] mb-1">Admin bootstrap</h1>
        <p className="text-sm text-gray-600 mb-5">
          One-shot self-service to grant the admin role to an existing account.
          Use when the seeded admin login is unreachable.
        </p>

        {!secret && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <p className="font-semibold">No secret in URL.</p>
            <p className="text-xs mt-0.5">
              Open this page via <code className="bg-amber-100 px-1 rounded">/admin-bootstrap?secret=...</code>
              {' '}with the value of <code className="bg-amber-100 px-1 rounded">ADMIN_BOOTSTRAP_SECRET</code> set on Render.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {result?.ok ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-900 font-semibold">
                {result.alreadyAdmin ? 'Already admin.' : 'Admin role granted.'}
              </p>
              <p className="text-green-800 text-sm mt-1">{result.message}</p>
            </div>
            <div className="p-4 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold mb-1">Next steps (do these in order):</p>
              <ol className="list-decimal list-inside space-y-1 text-[#1F3A68]/90">
                <li>Open <Link to="/settings" className="underline">Settings</Link> → Security → Enable 2FA.</li>
                <li>Scan the QR with your authenticator app and save the backup codes.</li>
                <li>Sign out and back in to confirm the 2FA challenge fires.</li>
                <li>Remove <code className="bg-[#1F3A68]/10 px-1 rounded">ADMIN_BOOTSTRAP_SECRET</code> on Render so this page returns 404.</li>
              </ol>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="w-full bg-[#1F3A68] text-white py-2.5 rounded-lg font-semibold hover:bg-[#1F3A68]/90"
            >
              Go to admin dashboard
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Email to promote</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="gregoryeke@brickandcapital.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
              />
              {user?.email && email !== user.email && (
                <p className="text-xs text-amber-700 mt-1">
                  Not the currently logged-in account ({user.email}).
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-[#1F3A68] text-white py-2.5 rounded-lg font-semibold hover:bg-[#1F3A68]/90 disabled:opacity-50"
            >
              {submitting ? 'Granting…' : 'Grant admin role'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminBootstrapPage;
